/**
 * About page – the band's story.
 * Features a parallax hero, clickable band-member cards, and multiple
 * scroll-reveal text sections grouped inside a ScrollRevealGroup.
 * Floating polaroid images add depth between story sections.
 */
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import ScrollRevealRichText from "@/components/ScrollRevealRichText";
import ScrollRevealGroup from "@/components/ScrollRevealGroup";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import FloatingImage from "@/components/FloatingImage";
import GrainOverlay from "@/components/GrainOverlay";
import PageFrame from "@/components/PageFrame";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import VariableHeadline from "@/components/VariableHeadline";
import BlurImage from "@/components/BlurImage";

// Import hero images for floating effect
import hero01 from "@/assets/hero/hero-01.jpg?format=webp&quality=85";
import hero02 from "@/assets/hero/hero-02.jpg?format=webp&quality=85";
import hero03 from "@/assets/hero/hero-03.jpg?format=webp&quality=85";
import hero04 from "@/assets/hero/hero-04.jpg?format=webp&quality=85";
import hero05 from "@/assets/hero/hero-05.jpg?format=webp&quality=85";
import hero06 from "@/assets/hero/hero-06.jpg?format=webp&quality=85";
import hero07 from "@/assets/hero/hero-07.jpg?format=webp&quality=85";
import hero08 from "@/assets/hero/hero-08.jpg?format=webp&quality=85";
import hero10 from "@/assets/hero/hero-10.jpg?format=webp&quality=85";

// External link component for inline links
const ExtLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a 
    href={href} 
    target="_blank" 
    rel="noopener noreferrer"
  >
    {children}
  </a>
);

const About = () => {
  useDocumentMeta({
    title: "About – Thursday Jam | Blues Rock Band from Berlin",
    description: "Meet the Bucher family band from Berlin. Learn about Sebastian, Hans-Joachim, and Robert – their story, sound, and musical roots.",
    canonical: "https://thursdayjam.rocks/about",
    ogImage: hero06,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll();
  
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  // About story paragraphs: intentionally generous line-height for readability
  const bodyText = "text-lg md:text-xl leading-[1.75rem] md:leading-[2rem]";
  const bodyTextMuted = `${bodyText} text-foreground`;

  return (
    <main id="main-content" ref={containerRef} className="relative min-h-screen bg-background">
      <PageFrame />
      <GrainOverlay />
      <Navigation variant="about" />

      {/* Hero section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden px-6 md:px-20">
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 opacity-20"
        >
          <BlurImage
            src={hero01}
            alt=""
            className="w-full h-[120%] object-cover grayscale"
            aria-hidden="true"
          />
        </motion.div>
        
        <div className="relative z-10 text-center px-8">
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.5, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto mb-8 h-px w-32 bg-foreground/30"
          />
          <VariableHeadline 
            as="h1" 
            pattern="wave"
            className="text-6xl md:text-8xl lg:text-9xl mb-6"
          >
            Our Story
          </VariableHeadline>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="text-sm uppercase tracking-[0.3em] text-muted-foreground"
          >
            Blues Rock · Classic Rock · Modern Roots
          </motion.p>
        </div>

        {/* Scroll indicator */}
        <motion.button
          onClick={() => document.getElementById("the-band-section")?.scrollIntoView({ behavior: "smooth" })}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="group absolute bottom-12 left-1/2 -translate-x-1/2 cursor-pointer"
          aria-label="Scroll to content"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-2"
          >
            <span className="font-display text-xs uppercase tracking-[0.2em] text-foreground/70 transition-colors duration-300 group-hover:text-foreground">
              Scroll
            </span>
            <div className="h-12 w-px bg-gradient-to-b from-foreground/50 to-transparent transition-all duration-300 group-hover:from-foreground group-hover:h-16" />
          </motion.div>
        </motion.button>
      </section>

      {/* The Band Section - Before the story */}
      <section id="the-band-section" className="relative py-12 px-6 md:px-24 lg:px-32 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mb-16"
          >
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="mb-6 h-px w-24 origin-left bg-foreground/30"
            />
            <VariableHeadline 
              as="h2" 
              pattern="edges"
              className="text-5xl md:text-7xl mb-4"
            >
              The Band
            </VariableHeadline>
            <p className="text-base text-muted-foreground">
              A family band from Berlin, Germany
            </p>
          </motion.div>

          {/* Band Members Grid */}
          <div className="grid gap-8 md:grid-cols-3">
            {[
              {
                name: "Sebastian Bucher",
                role: "Guitar & Vocals",
                image: hero06,
                anchor: "member-sebastian",
              },
              {
                name: "Hans-Joachim Bucher",
                role: "Drums, Percussion, Recording & Production",
                image: hero01,
                anchor: "member-hansjoachim",
              },
              {
                name: "Robert Bucher",
                role: "Bass, Brass & Backing Vocals",
                image: hero10,
                anchor: "member-robert",
              },
            ].map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.3 + index * 0.15, ease: [0.16, 1, 0.3, 1] }}
                role="button"
                tabIndex={0}
                aria-label={`Read about ${member.name}`}
                className="group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded-sm"
                onClick={() => document.getElementById(member.anchor)?.scrollIntoView({ behavior: "smooth" })}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); document.getElementById(member.anchor)?.scrollIntoView({ behavior: "smooth" }); } }}
              >
                <div className="relative overflow-hidden">
                  {/* Member Image */}
                  <div className="aspect-[3/4] overflow-hidden relative z-0">
                    <BlurImage
                      src={member.image}
                      alt={member.name}
                      className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105"
                    />
                  </div>

                  {/* Gradient Overlay — extends upward on hover */}
                  <div className="absolute bottom-0 left-0 right-0 z-10 h-[60%] bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-[height] duration-700 ease-in-out group-hover:h-full" />

                  {/* Member Info */}
                  <div className="absolute bottom-0 left-0 right-0 z-20 p-6">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                      {member.role}
                    </p>
                    <h3 className="font-display text-2xl tracking-tight text-foreground">
                      {member.name}
                    </h3>
                  </div>

                  {/* Hover line */}
                  <motion.div
                    className="absolute bottom-0 left-0 h-px bg-foreground"
                    initial={{ width: 0 }}
                    whileHover={{ width: "100%" }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story sections */}
      <ScrollRevealGroup className="relative">
        {/* Section 1: How It Started */}
        <section className="relative py-10 px-6 md:px-24 lg:px-32">
          <FloatingImage
            src={hero02}
            alt="Band rehearsal"
            className="w-32 md:w-48 right-4 md:right-[5%] top-32 hidden md:block"
            direction="left"
            parallaxSpeed={80}
            rotation={5}
          />
          
          <div className="max-w-2xl mx-auto space-y-4 relative z-10 px-4">
            <VariableHeadline 
              as="h2" 
              pattern="wave"
              className="text-4xl md:text-6xl mb-6"
            >
              How It Started
            </VariableHeadline>
          
            <ScrollRevealRichText className={bodyText}>
              It began the way the best things do: family, a shared love for music, and the question "why don't we just play together?"
              {"\n\n"}
              Hans-Joachim spent a lifetime listening to great records, absorbing everything from blues legends to classic rock icons. And when retirement came around, he finally sat down behind a drum kit – taking weekly lessons at{" "}
              <ExtLink href="https://drummerhood.de/">Drummerhood</ExtLink>
              {" "}to build a proper foundation. Turns out, decades of deep listening had prepared him well. His drive to learn and improve is what brought the family together to play.
              {"\n\n"}
              Sebastian had been making music since his teenage years – back then with his band "Die getrockneten Rosinen (D.G.R.)." But after the band dissolved, he stepped away from band life for many long years, channeling his creativity into producing catchy electronic music instead. It was only when Hans-Joachim started learning drums that Sebastian picked up the guitar again to support him – and rediscovered the raw joy of playing live with other people.
              {"\n\n"}
              Rob? He fell in love with music and especially brass after seeing{" "}
              <ExtLink href="https://www.youtube.com/watch?v=U3neuAxQMzA">
                Trombone Shorty live at a tiny concert at the Skulpturengarten der Neuen Nationalgalerie in Berlin back in 2011
              </ExtLink>
              , together with Hans-Joachim. That led to buying a trombone, an unsuccessful attempt with a teacher, and a few rehearsal sessions with only a few good notes. After the previous bass player suddenly left the band, Rob picked it up and was surprised how much easier it is to play the right notes. The brass instruments? Still a big love. Still a work in progress. Just triggered again with receiving a pocket trumpet as birthday present.
              {"\n\n"}
              Thursday became our day. We'd get together, write songs that came from somewhere real, play covers we loved, and just enjoy making noise together. Thursday Jam was born.
            </ScrollRevealRichText>
          </div>
          
          <FloatingImage
            src={hero03}
            alt="Live performance"
            className="w-28 md:w-40 left-4 md:left-[5%] bottom-48 hidden md:block"
            direction="right"
            parallaxSpeed={60}
            rotation={-4}
          />
        </section>

        {/* Section 2: An Ode to Music */}
        <section className="relative py-10 px-6 md:px-24 lg:px-32 bg-card/30">
          <FloatingImage
            src={hero04}
            alt="Stage lights"
            className="w-36 md:w-48 left-4 md:left-[5%] top-48 hidden md:block"
            direction="right"
            parallaxSpeed={70}
            rotation={3}
          />
          
          <div className="max-w-2xl mx-auto space-y-4 relative z-10 px-4">
            <VariableHeadline 
              as="h2" 
              pattern="edges"
              className="text-4xl md:text-6xl mb-6"
            >
              An Ode to Music
            </VariableHeadline>

            <ScrollRevealRichText className={bodyText}>
              Here's what we believe: all music is beautiful. If it was crafted with intent, if it moves someone, if it evokes emotions – it has a right to exist.
              {"\n\n"}
              We listen to everything. Blues, blues rock, rock, R&B, soul, brass house, southern rock, jazz fusion, dub, funk, hip-hop, electronic, folk, classical, reggae, pop. Music is music. It connects people across time, language, and experience.
              {"\n\n"}
              We just happen to mostly play blues rock. That's where our setup lands us, and honestly, where our skills feel most at home. Three-piece groove, guitar-driven melodies, songs that breathe. It's what fits in our hands.
            </ScrollRevealRichText>
          </div>

          <FloatingImage
            src={hero05}
            alt="Guitar close-up"
            className="w-32 md:w-44 right-4 md:right-[5%] bottom-32 hidden md:block"
            direction="left"
            parallaxSpeed={50}
            rotation={-5}
          />
        </section>

        {/* Section 3: The Sound */}
        <section className="relative py-10 px-6 md:px-24 lg:px-32">
          <FloatingImage
            src={hero06}
            alt="Drums"
            className="w-36 md:w-48 right-4 md:right-[5%] top-24 hidden md:block"
            direction="left"
            parallaxSpeed={90}
            rotation={4}
          />
          
          <div className="max-w-2xl mx-auto space-y-4 relative z-10 px-4">
            <VariableHeadline 
              as="h2" 
              pattern="center"
              className="text-4xl md:text-6xl mb-6"
            >
              The Sound
            </VariableHeadline>

            <ScrollRevealRichText className={bodyText}>
              Our roots are in blues rock – Stevie Ray Vaughan, Hendrix, Henrik Freischlader, Cream. Guitar-driven, expressive, built on feel and dynamics.
              {"\n\n"}
              Classic rock runs through everything: Neil Young, Fleetwood Mac, ZZ Top, Steve Miller Band. And underneath it all, groove. Dr. John's swamp funk, Fat Freddy's Drop's dub-soul vibe.
              {"\n\n"}
              Trips to New Orleans Jazz Fest shaped how we think about music. Trombone Shorty, Jon Batiste, Snarky Puppy, Lettuce – these artists showed us that music lives when it breathes, when players listen to each other.
              {"\n\n"}
              We love to jam. Create new melodies on the fly. Let solos stretch out. Have conversations between instruments. The name fits.
            </ScrollRevealRichText>
          </div>

          <FloatingImage
            src={hero07}
            alt="Bass guitar"
            className="w-28 md:w-40 left-4 md:left-[5%] bottom-64 hidden md:block"
            direction="right"
            parallaxSpeed={65}
            rotation={-3}
          />
        </section>

        {/* Section 4: The Band */}
        <section className="relative py-10 px-6 md:px-24 lg:px-32 bg-card/30">
          <FloatingImage
            src={hero08}
            alt="Family jam"
            className="w-32 md:w-44 left-4 md:left-[5%] top-32 hidden md:block"
            direction="right"
            parallaxSpeed={75}
            rotation={6}
          />
          
          <div className="max-w-2xl mx-auto space-y-4 relative z-10 px-4">
            <VariableHeadline 
              as="h2" 
              pattern="edges"
              className="text-4xl md:text-6xl mb-6"
            >
              The Band
            </VariableHeadline>

            <div className="space-y-16">
              <div id="member-sebastian" className="scroll-mt-32">
                <h3 className="text-xl md:text-2xl font-bold mb-2">Sebastian "Bassi" Bucher</h3>
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Guitar & Vocals</p>
                <ScrollRevealRichText className={bodyText}>
                  Sebastian started playing in his teenage years with his band "Die getrockneten Rosinen (D.G.R.)." After the band dissolved, he didn't play in a band for many long years. It was only when Hans-Joachim started learning drums that Sebastian picked up the guitar again to support him – and rediscovered the joy of making music together. The musical backbone and creative engine. When a song needs direction, Sebastian finds it.
                </ScrollRevealRichText>
              </div>

              <div id="member-hansjoachim" className="scroll-mt-32">
                <h3 className="text-xl md:text-2xl font-bold mb-2">Hans-Joachim "Hajobu" Bucher</h3>
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Drums, Percussion, Recording & Production</p>
                <ScrollRevealRichText className={bodyText}>
                  Hans-Joachim spent a lifetime absorbing great music – from blues legends to classic rock icons. When retirement came around, he finally sat down behind a drum kit and started taking lessons. Decades of deep listening had prepared him well. His enthusiasm for learning drums is what brought the family together to play, and ultimately led to the creation of Thursday Jam. As an avid concert-goer and photography enthusiast, he's captured countless live moments on his{" "}
                  <ExtLink href="https://www.youtube.com/@hans-joachimbucher6877/videos">
                    YouTube channel
                  </ExtLink>
                  . Always in the first row – which is how{" "}
                  <ExtLink href="https://youtu.be/otaqqNVNqPI?si=zVhBuSG04dJBd_0U&t=2669">
                    Trombone Shorty ended up grabbing his camera in Erlangen to film the crowd
                  </ExtLink>
                  . Beyond playing, he handles recording and production for the band.
                </ScrollRevealRichText>
              </div>

              <div id="member-robert" className="scroll-mt-32">
                <h3 className="text-xl md:text-2xl font-bold mb-2">Robert "Rob" Bucher</h3>
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Bass, Brass & Backing Vocals</p>
                <ScrollRevealRichText className={bodyText}>
                  Fell in love with music and especially brass after seeing{" "}
                  <ExtLink href="https://www.youtube.com/watch?v=U3neuAxQMzA">
                    Trombone Shorty live in Berlin in 2011
                  </ExtLink>
                  , together with Hans-Joachim. That led to buying a trombone, an unsuccessful attempt with a teacher, and a few rehearsal sessions with only a few good notes. When the previous bass player left, Rob picked up the bass – and was surprised how much easier it is to play the right notes. The brass instruments? Still a big love. Still a work in progress. Just triggered again with receiving a pocket trumpet as birthday present. Also the guy who recorded{" "}
                  <ExtLink href="https://www.youtube.com/watch?v=OYzHgnv82RI">
                    Too Many Zooz at YAAM Berlin
                  </ExtLink>
                  {" "}– 360,000+ views and counting.
                </ScrollRevealRichText>
              </div>
            </div>

            <div className="mt-20 pt-20 border-t border-border">
              <h3 className="text-lg md:text-xl font-bold mb-2 text-muted-foreground">Past Members</h3>
              <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Bernhard Bornschein – Keys, guitar, flute, sax</p>
              <ScrollRevealRichText className={bodyText}>
                A major part of the band's history who brought multi-instrumental depth and texture to our sound. His contributions shaped what Thursday Jam became.
              </ScrollRevealRichText>
            </div>
          </div>
        </section>

        {/* Section 5: Family & Friends */}
        <section className="relative py-10 px-6 md:px-24 lg:px-32">
          <div className="max-w-2xl mx-auto space-y-4 relative z-10 px-4">
            <VariableHeadline 
              as="h2" 
              pattern="random"
              className="text-4xl md:text-6xl mb-6"
            >
              Family & Friends
            </VariableHeadline>

            <ScrollRevealRichText className={bodyText}>
              Over the years, we've had guest musicians join us on stage and at rehearsal sessions. But most importantly: we try to introduce the kids of our family to the world of making music.
              {"\n\n"}
              Getting a guitar in young hands, letting them feel a rhythm, watching them discover that they can make something – that's the real magic. We're deeply inspired by the New Orleans tradition of family music, where songs are passed down through generations and playing together is just what families do. Music is a universal language – everybody can understand it, no translation needed.
              {"\n\n"}
              Thursday Jam is, at its heart, a family affair. We spend time together, we get creative, we laugh when things go sideways, and sometimes we stumble onto something beautiful.
            </ScrollRevealRichText>
          </div>
        </section>

        {/* Closing section */}
        <section className="relative py-12 px-6 md:px-20 flex items-center justify-center">
          <div className="text-center max-w-4xl mx-auto">
            <motion.blockquote
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2 }}
              className="font-display font-[800] text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] text-foreground leading-tight"
            >
              "Thursday Jam plays soulful Blues Rock – rooted in classic guitar music, powered by modern groove, and held together by the simple joy of playing music with people you love."
            </motion.blockquote>

            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.5 }}
              className="mx-auto mt-16 h-px w-32 bg-foreground/30"
            />

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
              className="mt-16"
            >
              <Link
                to="/"
                className="inline-flex items-center gap-3 font-body text-xs uppercase tracking-[0.3em] text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft size={16} />
                Back to Home
              </Link>
            </motion.div>
          </div>
        </section>
      </ScrollRevealGroup>
      <Footer />
    </main>
  );
};

export default About;
